/**
 * ErrorBoundary — top-level catch for render-time errors.
 *
 * Without this, any thrown error in a route component white-screens the
 * whole SPA. Wraps <App/> in main.tsx and shows a recoverable fallback.
 */

import { Component, type ReactNode } from 'react';
import { Button } from '@/acc-ui/primitives/Button';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-surface-page">
          <h1 className="text-title font-bold text-on-surface">Something went wrong</h1>
          <p className="text-caption text-on-surface-variant max-w-md leading-relaxed">
            The page hit an unexpected error. Reloading usually clears it. If it keeps happening,
            let an administrator know.
          </p>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
